'use client'
import {
  ArrowLeftRight,
  Calendar,
  Calendar1,
  LayoutDashboard,
  LayoutDashboardIcon,
  ListChecks,
  Mail,
  Notebook,
  Settings,
  Star,
  StickyNote,
  User,
  Users,
} from 'lucide-react'

import Image from 'next/image'
import { ReactNode } from 'react'
export const demoColors = [
  '#D50000',
  '#F4511E',
  '#33B679',
  '#3F51B5',
  '#8E24AA',
]
export const demoNames = [
  // General & Funny Students
  'My Worst Student 😬',
  'Future Prodigy 🎻',
  'The Chronic Canceller 📅',
  'The Overachiever 🏆',
  'The Human Wikipedia 📚',
  'The Excuse Generator 🤷‍♂️',
  'Mom Made Me Do This 🙃',
  'Late But Enthusiastic ⏳',
  'Tells Stories Instead of Learning 📖',
  'Needs a Second to Warm Up 🔥',
  'The One Who Overthinks Everything 🤯',
  'Thinks They Already Know It All 🎓',
  'Sleeps Through Class 😴',
  'Asks Too Many ‘What If’ Questions ❓',
  'Brings Snacks to Every Lesson 🍕',
  'Always Forgets a Pencil ✏️',
  'Just Here for the Fun 😆',
  'Secretly Smarter Than the Teacher 🤐',
  "Can't Sit Still for 5 Minutes 🏃",
  'Talks More Than They Learn 🗣️',
  'Thinks Homework is Optional 📚',

  // Academic / Tutoring-Focused
  'The Human Calculator 🧮',
  'Google’s #1 Competitor 🔍',
  "Knows the Answer But Won't Say It 🤫",
  'Asks for Extra Credit Every Time 🎟️',
  'Needs a 3rd Reminder to Focus 👀',
  'Always Raising Their Hand ✋',
  'Loves Theory, Hates Application 📖',
  "Swears They Studied But Didn't 🤦",
  'The Future Debate Champion 🎤',
  'Hides in the Back of the Room 🏃‍♂️',
  'Thinks Spelling Doesn’t Matter ✍️',

  // Learning & Skill-Based
  'Fast Learner, Forgets Fast Too 🔄',
  'Tries Hard, Still Confused 🤯',
  'Thinks Memorization is Studying 🧠',
  'Over-Explains Every Answer 💬',
  'Knows Every Random Fact Ever 📜',
  'Asks Why More Than a Toddler 🤷‍♂️',
  'Loves Group Work, Does No Work 👥',
  'Will Ace the Test, but No Homework ✏️',
]

export const calendarColors = [
  { label: 'Tomato', value: '#D50000', apiNumber: 11 }, // Red
  { label: 'Flamingo', value: '#E67C73', apiNumber: 4 },
  { label: 'Tangerine', value: '#F4511E', apiNumber: 6 }, // Orange
  { label: 'Banana', value: '#F6BF26', apiNumber: 5 }, // Pinkish (close to Red/Violet)
  { label: 'Sage', value: '#33B679', apiNumber: 2 },
  { label: 'Basil', value: '#0B8043', apiNumber: 10 }, // Green
  { label: 'Peacock', value: '#039BE5', apiNumber: 7 }, // Blue
  { label: 'Blueberry', value: '#3F51B5', apiNumber: 9 }, // Indigo
  { label: 'Lavender', value: '#7986CB', apiNumber: 1 }, // Violet
  { label: 'Grape', value: '#8E24AA', apiNumber: 3 }, // Purple variant (Violet)
  { label: 'Graphite', value: '#616161', apiNumber: 8 }, // Gray/Neutral
]

export const StudentImportColumnMatcherSelectValues: {
  [key: string]: { label: string; required: boolean }
} = {
  firstName: { label: 'Student First Name', required: true },
  lastName: { label: 'Student Last Name', required: true },
  studentPhoneNumber: { label: 'Student Phone Number', required: false },
  studentEmailAddress: { label: 'Student Email Address', required: false },
  pricePerLesson: { label: 'Default Price Per Lesson', required: false },
  lessonLength: { label: 'Default Lesson Length', required: false },
  primaryContactFirstName: {
    label: 'Primary Contact First Name',
    required: true,
  },
  primaryContactLastName: {
    label: 'Primary Contact Last Name',
    required: true,
  },
  primaryContactEmailAddress: {
    label: 'Primary Contact Email Address',
    required: false,
  },
  primaryContactPhoneNumber: {
    label: 'Primary Contact Phone Number',
    required: false,
  },
  secondaryContactFirstName: {
    label: 'Secondary Contact First Name',
    required: false,
  },
  secondaryContactLastName: {
    label: 'Secondary Contact Last Name',
    required: false,
  },
  secondaryContactEmailAddress: {
    label: 'Secondary Contact Email Address',
    required: false,
  },
  secondaryContactPhoneNumber: {
    label: 'Secondary Contact Phone Number',
    required: false,
  },
}

export const headerLinks: { title: string; href: string; icon: ReactNode }[] = [
  {
    icon: <LayoutDashboardIcon className="text-primary" />,
    title: 'Dashboard',
    href: '/home',
  },
  {
    icon: <Calendar className="text-primary" />,
    title: 'Calendar',
    href: '/calendar',
  },
  {
    icon: <User className="text-primary" />,
    title: 'Students',
    href: '/students',
  },
  { icon: <Star className="text-primary" />, title: 'Events', href: '/events' },
  {
    icon: <Users className="text-primary" />,
    title: 'Accounts',
    href: '/accounts',
  },
  {
    icon: <ArrowLeftRight className="text-primary" />,
    title: 'Transactions',
    href: '/transactions',
  },
  {
    icon: <StickyNote className="text-primary" />,
    title: 'Invoices',
    href: '/invoices',
  },
  {
    icon: <Users className="text-primary" />,
    title: 'Groups',
    href: '/groups',
  },
  {
    icon: <ListChecks className="text-primary" />,
    title: 'Attendance',
    href: '/attendance',
  },
  {
    icon: <ListChecks className="text-primary" />,
    title: 'Messages',
    href: '/messages',
  },
  {
    icon: <Settings className="text-primary" />,
    title: 'Settings',
    href: '/settings',
  },
]
export const integrationSidebarData = {
  navIntegrations: [
    {
      title: 'Integrations',
      url: '/settings',
      items: [
        {
          title: 'Calendly',
          url: '/integrations/calendly',
          icon: (
            <Image
              src="/calendly_brand.png"
              alt="Calendly logo"
              width={16}
              height={16}
              className="rounded-sm"
            />
          ),
        },
      ],
    },
  ],
}
export const sidebarData = {
  navMain: [
    {
      title: 'General',
      url: '/home',
      items: [
        {
          title: 'Dashboard',
          url: '/home',
          icon: (
            <LayoutDashboard className="!h-4 !w-4 flex-shrink-0" size={14} />
          ),
          tourId: '/home',
        },
        {
          title: 'Calendar',
          url: '/calendar',
          icon: <Calendar className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/calendar',
        },
        {
          title: 'Students',
          url: '/students',
          icon: <User className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/students',
        },
        {
          title: 'Notes',
          url: '/notes',
          icon: <Notebook className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/notes',
        },
        {
          title: 'Events',
          url: '/events',
          icon: <Star className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/events',
        },
        {
          title: 'Groups',
          url: '/groups',
          icon: <Users className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/groups',
        },
        {
          title: 'Attendance',
          url: '/attendance',
          icon: <ListChecks className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/attendance',
        },
        {
          title: 'Messages',
          url: '/messages',
          icon: <Mail className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/messages',
        },
        {
          title: 'Settings',
          url: '/settings',
          icon: <Settings className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/settings',
        },
      ],
    },
    {
      title: 'Finance',
      url: '#',
      items: [
        {
          title: 'Accounts',
          url: '/accounts',
          icon: <Users className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/accounts',
        },
        {
          title: 'Transactions',
          url: '/transactions',
          icon: (
            <ArrowLeftRight className="!h-4 !w-4 flex-shrink-0" size={14} />
          ),
          tourId: '/transactions',
        },
        {
          title: 'Invoices',
          url: '/invoices',
          icon: <StickyNote className="!h-4 !w-4 flex-shrink-0" size={14} />,
          tourId: '/invoices',
        },
      ],
    },
  ],
}
export const IntegrationLinks: {
  title: string
  href: string
  icon: ReactNode
}[] = [
  {
    icon: <Calendar1 className="text-primary" />,
    title: 'Calendly',
    href: '/integrations/calendly',
  },
]

// consts.ts

export const times = Array.from({ length: 24 }, (_, i) => {
  const hour = i % 12 === 0 ? 12 : i % 12 // Convert to 12-hour format
  const ampm = i < 12 ? 'AM' : 'PM' // Determine AM/PM
  return `${hour}:00${ampm}`
})
